import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const CFDRiskDisclosureModal = ({ open, onClose, onAccept }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isAccepted, setIsAccepted] = useState(false);

  const handleAccept = () => {
    if (isAccepted) {
      onAccept();
      onClose();
    }
  };

  const handleClose = () => {
    if (!isAccepted) {
      // Don't allow closing without acceptance
      return;
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: 2,
          maxWidth: isMobile ? '95vw' : '600px',
          maxHeight: isMobile ? '90vh' : 'auto'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          pt: 3,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningIcon 
            color="warning" 
            sx={{ fontSize: 28 }}
          />
          <Typography variant="h6" component="div" fontWeight={600}>
            Acknowledgment
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ 
            color: 'text.secondary',
            opacity: isAccepted ? 1 : 0.3,
            cursor: isAccepted ? 'pointer' : 'not-allowed'
          }}
          disabled={!isAccepted}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              lineHeight: 1.6,
              color: 'text.primary',
              fontSize: '1rem',
              fontWeight: 500,
              textAlign: 'center',
              px: 2
            }}
          >
            CFDs are complex and risky products. Trading on margin is highly risky – you can lose all of the funds you invest. Make sure you understand these risks and that CFDs are suitable for you before trading.
          </Typography>
        </Box>

        <Box sx={{ 
          border: '2px solid',
          borderColor: theme.palette.warning.main,
          borderRadius: 2,
          p: 2,
          backgroundColor: theme.palette.warning.light + '10',
          mb: 3
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.warning.dark,
              fontWeight: 500,
              textAlign: 'center',
              fontSize: '0.9rem'
            }}
          >
            ⚠️ Important: Please read and understand this risk disclosure before proceeding with CFD trading.
          </Typography>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={isAccepted}
              onChange={(e) => setIsAccepted(e.target.checked)}
              color="primary"
              sx={{ 
                '&.Mui-checked': {
                  color: theme.palette.primary.main
                }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              I have read and understood the above-stated CFD Risk Disclosure and acknowledge the same.
            </Typography>
          }
          sx={{ 
            alignItems: 'flex-start',
            '& .MuiFormControlLabel-label': {
              mt: 0.5
            }
          }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button
          onClick={handleAccept}
          variant="contained"
          color="primary"
          disabled={!isAccepted}
          startIcon={<CheckCircleIcon />}
          sx={{ 
            minWidth: 140,
            fontWeight: 600,
            fontSize: '1rem',
            py: 1.5,
            opacity: isAccepted ? 1 : 0.6,
            '&:disabled': {
              backgroundColor: theme.palette.grey[400],
              color: theme.palette.grey[600]
            }
          }}
        >
          I Agree
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CFDRiskDisclosureModal;